import { useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Link } from 'react-router-dom'
import { HelpCircle, X, BookOpen, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react'
import { useHelpDrawer } from '../hooks/useHelpDrawer'
import { GuideTree } from './content/GuideTree'
import { RichMarkdown } from './content/RichMarkdown'
import { cn } from '../lib/utils'

export default function HelpDrawer() {
  const {
    isOpen,
    openDrawer,
    closeDrawer,
    guideIndex,
    currentSection,
    setCurrentSection,
    sectionContent,
    loading,
    indexLoading,
  } = useHelpDrawer()

  const [treeCollapsed, setTreeCollapsed] = useState(false)

  return (
    <>
      {/* Trigger button - fixed bottom-right */}
      {!isOpen && (
        <button
          onClick={openDrawer}
          className="fixed bottom-6 right-6 z-40 w-10 h-10 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center"
          aria-label="Open help"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      )}

      {/* Drawer */}
      <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) closeDrawer() }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/30 z-50 drawer-overlay-enter" />
          <Dialog.Content
            className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-card border-l border-border shadow-xl flex flex-col drawer-enter outline-none"
            aria-describedby={undefined}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-primary" />
                <Dialog.Title className="font-semibold text-sm">
                  Help
                </Dialog.Title>
                {sectionContent && (
                  <span className="text-xs text-muted-foreground">
                    — {sectionContent.title}
                  </span>
                )}
              </div>
              <Dialog.Close asChild>
                <button
                  className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                  aria-label="Close help"
                >
                  <X className="w-4 h-4" />
                </button>
              </Dialog.Close>
            </div>

            {/* Tree navigation (collapsible) */}
            {guideIndex && guideIndex.sections.length > 0 && (
              <div className="border-b border-border flex-shrink-0">
                <button
                  onClick={() => setTreeCollapsed(!treeCollapsed)}
                  className="w-full flex items-center justify-between px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  <span>SECTIONS</span>
                  {treeCollapsed ? (
                    <ChevronDown className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronUp className="w-3.5 h-3.5" />
                  )}
                </button>
                {!treeCollapsed && (
                  <div className="px-4 pb-3 max-h-48 overflow-y-auto">
                    <GuideTree
                      sections={guideIndex.sections}
                      activeSlug={currentSection}
                      onSelect={setCurrentSection}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Index loading state */}
            {indexLoading && (
              <div className="px-4 py-3 border-b border-border flex-shrink-0">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-3 h-3 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
                  Loading sections...
                </div>
              </div>
            )}

            {/* Content area */}
            <div
              className={cn('flex-1 overflow-y-auto px-4 py-4')}
              key={currentSection}
            >
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-3 h-3 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
                  Loading...
                </div>
              ) : sectionContent ? (
                <RichMarkdown content={sectionContent.content} />
              ) : (
                <p className="text-sm text-muted-foreground">
                  Select a section to view help content.
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-border flex-shrink-0">
              <Link
                to={`/guide/${currentSection}`}
                onClick={closeDrawer}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                View in full guide
              </Link>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}
